import { useState, useCallback } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

// Helper function to convert blob to data URL
const blobToDataURL = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

const useVideo = () => {
  const [loading, setLoading] = useState(false);
  const [ffmpeg, setFFmpeg] = useState(null);

  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [trimmedVideoUrl, setTrimmedVideoUrl] = useState(null);
  const [isTrimming, setIsTrimming] = useState(false);

  // const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
  // const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/esm";
  const baseURL = "http://localhost:3000/ffmpeg";

  // Initialize FFmpeg
  const initFFmpeg = async () => {
    if (ffmpeg) return ffmpeg;

    const ffmpegInstance = new FFmpeg();
    
    await ffmpegInstance.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
      wasmURL: await toBlobURL( `${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
      workerURL: await toBlobURL( `${baseURL}/ffmpeg.worker.js`, "text/javascript"),
    });

    setFFmpeg(ffmpegInstance);
    return ffmpegInstance;
  };

  // Define getFrames as a callback function
  const getFrames = useCallback(async (recorded: any) => {
    try {
      if(!recorded) return;
      setLoading(true);
      const ffmpegInstance = await initFFmpeg();
      const randomId = Math.floor(Math.random() * 999999);
      const dirName = `thumbnails-${randomId}`;

      const { file } = recorded;
      
      
      const fileData = await fetchFile(recorded);
      await ffmpegInstance.writeFile("input.webm", fileData);
      await ffmpegInstance.createDir(dirName);

      await ffmpegInstance.exec([
        "-ss", "1",
        "-i", "input.webm",
        "-r", "1",
        "-f", "image2",
        "-vf", "scale=320:-1",
        "-q:v", "2",
        "-preset", "ultrafast",
        `${dirName}/%04d.png`,
      ]);

      // Get all frames from directory
      const dirContents = await ffmpegInstance.listDir(dirName);
      const framesFiles = dirContents.filter(file => !file.isDir);

      const frames = [];
      // Process each frame
      for (const framesFile of framesFiles) {
        const frameDataArray = await ffmpegInstance.readFile(`${dirName}/${framesFile.name}`);
        const frameBlob = new Blob([frameDataArray], { type: 'image/png' });
        const frame = await blobToDataURL(frameBlob);
        frames.push(frame);
      }
      
      // Cleanup
      await ffmpegInstance.deleteFile("input.webm");
      for (const framesFile of framesFiles) {
        await ffmpegInstance.deleteFile(`${dirName}/${framesFile.name}`);
      }
      await ffmpegInstance.deleteDir(dirName);
      
      return frames;
    } catch (error) {
      console.error('Error extracting frames:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [ffmpeg]);

  const trimVideo = useCallback(async (startTime: number, endTime: number, videoFile: any): Promise<any> => {

    if (!videoFile) return;

    setEndTime(Math.floor(videoFile?.duration || 0));
    setIsTrimming(true);
    try {
        const ffmpegInstance = await initFFmpeg();
        
        
        await ffmpegInstance.writeFile('input.mp4', new Uint8Array(await videoFile.arrayBuffer()));

        
        await ffmpegInstance.exec([
            '-i', 'input.mp4',
            '-ss', String(startTime),
            '-to', String(endTime),
            '-c', 'copy',
            '-avoid_negative_ts', '1',
            'output.mp4'
        ]);

        // Read output file
        const data = await ffmpeg.readFile('output.mp4');
        const blob = new Blob([data], { type: 'video/mp4' });
        // const url = URL.createObjectURL(blob);
        
        // setTrimmedVideoUrl(url);
        return blob;
    } catch (error) {
        console.error('Trimming failed:', error);
        return false;
    } finally {
      setIsTrimming(false);
    }
  }, [ffmpeg]);

  return { loading, initFFmpeg, getFrames, trimVideo, startTime, endTime,  trimmedVideoUrl, isTrimming };
};

export default useVideo;